import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api, { emailPreferencesAPI, gdprAPI } from '../services/api';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import Modal from '../components/ui/Modal';
import Input from '../components/ui/Input';
import { formatDate } from '../utils/dateFormat';
import { useToast } from '../context/ToastContext';
import ChangePasswordModal from '../components/ChangePasswordModal';
import pushNotifications from '../utils/pushNotifications';

const MyProfile = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminderEnabled, setReminderEnabled] = useState(true);
  const [emailNotifEnabled, setEmailNotifEnabled] = useState(true);
  const [emailDigestEnabled, setEmailDigestEnabled] = useState(true);
  // Bug #5: modalul de schimbare parolă există dar nu era linkat nicăieri.
  const [showChangePassword, setShowChangePassword] = useState(false);
  // GDPR — export date personale + ștergere/anonimizare cont.
  const [exportingData, setExportingData] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const res = await gdprAPI.exportMyData();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'export-date-personale.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      showToast(t('gdprData.exportSuccess'), 'success');
    } catch (e) {
      showToast(e.response?.data?.error || t('gdprData.exportError'), 'error');
    } finally {
      setExportingData(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;
    setDeletingAccount(true);
    try {
      await gdprAPI.deleteMyAccount(deletePassword);
      try {
        sessionStorage.setItem('logoutMessage', t('gdprData.deleteSuccess'));
      } catch (_) {}
      logout();
    } catch (e) {
      showToast(e.response?.data?.error || t('gdprData.deleteError'), 'error');
      setDeletingAccount(false);
    }
  };

  // Push notifications — sincronizate cu state-ul real al subscription-ului din browser.
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    setPushSupported(pushNotifications.isSupported());
    pushNotifications.hasActiveSubscription().then(setPushEnabled);
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        const r = await pushNotifications.unsubscribe();
        if (r.ok) {
          setPushEnabled(false);
          showToast(t('myProfile.pushDisabledToast'), 'success');
        } else {
          showToast(t('myProfile.pushErrorToast'), 'error');
        }
      } else {
        const r = await pushNotifications.subscribe();
        if (r.ok) {
          setPushEnabled(true);
          showToast(t('myProfile.pushEnabledToast'), 'success');
        } else if (r.error === 'permission_denied') {
          showToast(t('myProfile.pushPermissionDeniedToast'), 'error');
        } else {
          showToast(t('myProfile.pushErrorToast'), 'error');
        }
      }
    } finally {
      setPushBusy(false);
    }
  };
  const navigate = useNavigate();

  // Self-request modals
  const [showReqName, setShowReqName] = useState(false);
  const [reqNameNew, setReqNameNew] = useState('');
  const [reqNameReason, setReqNameReason] = useState('');
  const [reqNameFile, setReqNameFile] = useState(null);
  const [submittingName, setSubmittingName] = useState(false);

  const [showReqRoute, setShowReqRoute] = useState(false);
  const [reqRouteForm, setReqRouteForm] = useState({
    homeAddress: '',
    workAddress: '',
    transportMode: '',
    routeDetails: '',
    routeDuration: '',
  });
  const [reqRouteReason, setReqRouteReason] = useState('');
  const [reqRouteFile, setReqRouteFile] = useState(null);
  const [submittingRoute, setSubmittingRoute] = useState(false);

  const handleSubmitNameRequest = async () => {
    if (!reqNameNew.trim()) {
      showToast(t('changeData.errorMissingName'), 'error');
      return;
    }
    setSubmittingName(true);
    try {
      const fd = new FormData();
      fd.append('newLastName', reqNameNew.trim());
      if (reqNameReason.trim()) fd.append('reason', reqNameReason.trim());
      if (reqNameFile) fd.append('supportingDoc', reqNameFile);
      await api.post('/employees/me/request-name-change', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('myProfile.requestCreatedToast'), 'success');
      setShowReqName(false);
      setReqNameNew('');
      setReqNameReason('');
      setReqNameFile(null);
      // Reîncarcă pagina ca Layout să prindă PENDING_EMPLOYEE_SIGNATURE și să afișeze popup-ul blocant
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 400);
    } catch (err) {
      showToast(err.response?.data?.error || t('changeData.errorGeneric'), 'error');
    } finally {
      setSubmittingName(false);
    }
  };

  const openRouteRequest = () => {
    setReqRouteForm({
      homeAddress: employee?.homeAddress || '',
      workAddress: employee?.workAddress || '',
      transportMode: employee?.transportMode || '',
      routeDetails: employee?.routeDetails || '',
      routeDuration: employee?.routeDuration != null ? String(employee.routeDuration) : '',
    });
    setReqRouteReason('');
    setReqRouteFile(null);
    setShowReqRoute(true);
  };

  const handleSubmitRouteRequest = async () => {
    setSubmittingRoute(true);
    try {
      const fd = new FormData();
      Object.entries(reqRouteForm).forEach(([k, v]) => {
        if (v !== undefined && v !== null) fd.append(k, v);
      });
      if (reqRouteReason.trim()) fd.append('reason', reqRouteReason.trim());
      if (reqRouteFile) fd.append('supportingDoc', reqRouteFile);
      await api.post('/employees/me/request-route-change', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('myProfile.requestCreatedToast'), 'success');
      setShowReqRoute(false);
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 400);
    } catch (err) {
      showToast(err.response?.data?.error || t('changeData.errorRouteGeneric'), 'error');
    } finally {
      setSubmittingRoute(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    // Încarcă starea toggle-ului de reminder din user
    if (user) {
      api
        .get('/users/me')
        .then((res) => {
          if (res.data?.receiveIPReminders !== undefined) {
            setReminderEnabled(res.data.receiveIPReminders);
          }
        })
        .catch(() => {});
      emailPreferencesAPI
        .get()
        .then((res) => {
          setEmailNotifEnabled(res.data.emailNotificationsEnabled);
          setEmailDigestEnabled(res.data.emailDigestEnabled);
        })
        .catch(() => {});
    }
  }, [user]);

  const handleToggleReminder = async () => {
    const newValue = !reminderEnabled;
    setReminderEnabled(newValue);
    try {
      await api.put('/notifications/toggle-reminders', { enabled: newValue });
    } catch (err) {
      setReminderEnabled(!newValue); // revert
    }
  };

  const handleToggleEmailNotif = async () => {
    const newValue = !emailNotifEnabled;
    setEmailNotifEnabled(newValue);
    try {
      await emailPreferencesAPI.update({ emailNotificationsEnabled: newValue });
    } catch (err) {
      setEmailNotifEnabled(!newValue);
    }
  };

  const handleToggleEmailDigest = async () => {
    const newValue = !emailDigestEnabled;
    setEmailDigestEnabled(newValue);
    try {
      await emailPreferencesAPI.update({ emailDigestEnabled: newValue });
    } catch (err) {
      setEmailDigestEnabled(!newValue);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await api.get('/employees/me');
      setEmployee(response.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const transportLabels = {
    personal: t('myProfile.transport.personal'),
    public: t('myProfile.transport.public'),
    mixt: t('myProfile.transport.mixt'),
    pe_jos: t('myProfile.transport.walk'),
    bicicleta: t('myProfile.transport.bike'),
    car: t('myProfile.transport.personal'),
    public_transport: t('myProfile.transport.public'),
    bicycle: t('myProfile.transport.bike'),
    walking: t('myProfile.transport.walk'),
    motorcycle: t('myProfile.transport.motorcycle'),
  };

  if (loading) {
    return <Spinner label={t('myProfile.loading')} />;
  }

  if (!employee) {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        <PageHeader title={t('myProfile.title')} subtitle={t('myProfile.subtitleNoEmp')} />
        <Card>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
            {t('myProfile.personalInfo')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="input-label">{t('myProfile.firstName')}</label>
              <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                {user.firstName}
              </div>
            </div>
            <div>
              <label className="input-label">{t('myProfile.lastName')}</label>
              <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                {user.lastName}
              </div>
            </div>
            <div>
              <label className="input-label">{t('myProfile.email')}</label>
              <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                {user.email}
              </div>
            </div>
            <div>
              <label className="input-label">{t('myProfile.role')}</label>
              <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                {user.realRole || user.role}
              </div>
            </div>
          </div>

          {/* Calificări/flag-uri ortogonale rolului (isCLM, isSSMInstructor, isCadruTehnicPSI).
              Anterior nu apăreau pe propriul profil — admin-ul nu vedea că e bifat CLM, deși
              pe vederea SSM-istului apărea corect (citită din DB). */}
          {(user?.isCLM ||
            user?.isSSMInstructor ||
            user?.isCadruTehnicPSI ||
            user?.isSignatureResponsible) && (
            <div className="mt-4">
              <label className="input-label">{t('myProfile.qualificationsLabel')}</label>
              <div className="flex flex-wrap gap-2">
                {user.isSSMInstructor && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 dark:bg-blue-950/50 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                    {t('myProfile.roleInspectorSSM')}
                  </span>
                )}
                {user.isCadruTehnicPSI && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 dark:bg-red-950/50 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                    {t('myProfile.roleCadruTehnicPSI')}
                  </span>
                )}
                {user.isCLM && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                    {t('myProfile.roleCLM')}
                  </span>
                )}
                {user.isSignatureResponsible && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                    {t('myProfile.roleSignatureResponsible')}
                  </span>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Securitate — schimbare parolă (Bug #5), disponibilă și conturilor fără fișă de angajat */}
        <Card>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('myProfile.security')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('myProfile.securityDesc')}
              </p>
            </div>
            <Button variant="primary" onClick={() => setShowChangePassword(true)}>
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
              {t('myProfile.changePassword')}
            </Button>
          </div>
        </Card>

        {showChangePassword && (
          <ChangePasswordModal
            onClose={() => setShowChangePassword(false)}
            onSuccess={() => setShowChangePassword(false)}
          />
        )}
      </div>
    );
  }

  // Statistici corecte
  const assigned = employee._count?.assignments || 0;
  const completed = employee._count?.trainings || 0;
  const completionRate = assigned > 0 ? Math.min(100, Math.round((completed / assigned) * 100)) : 0;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <PageHeader title={t('myProfile.title')} subtitle={t('myProfile.subtitle')} />

      {/* Informații Personale */}
      <Card>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {t('myProfile.personalInfo')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="input-label">{t('myProfile.firstName')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {user.firstName}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.lastName')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {user.lastName}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.email')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {user.email}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.phone')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {employee.user.phone || t('myProfile.notFilled')}
            </div>
          </div>
        </div>
      </Card>

      {/* Securitate — schimbare parolă (Bug #5) */}
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('myProfile.security')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('myProfile.securityDesc')}
            </p>
          </div>
          <Button variant="primary" onClick={() => setShowChangePassword(true)}>
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
              />
            </svg>
            {t('myProfile.changePassword')}
          </Button>
        </div>
      </Card>

      {/* Informații Angajare */}
      <Card>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {t('myProfile.employmentInfo')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="input-label">{t('myProfile.jobTitle')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {employee.jobTitle}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.department')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {employee.department?.name || t('myProfile.notSpecified')}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.hireDate')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {employee.hireDate ? formatDate(employee.hireDate) : t('myProfile.notSpecified')}
            </div>
          </div>
          <div>
            <label className="input-label">{t('myProfile.company')}</label>
            <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
              {employee.company?.name}
            </div>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReqNameNew('');
              setReqNameReason('');
              setReqNameFile(null);
              setShowReqName(true);
            }}
          >
            {t('myProfile.requestNameChange')}
          </Button>
          <Button variant="outline" size="sm" onClick={openRouteRequest}>
            {t('myProfile.requestRouteChange')}
          </Button>
        </div>
      </Card>

      {/* Traseu Domiciliu-Serviciu */}
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {t('myProfile.route')}
          </h2>
          <Button variant="primary" size="sm" onClick={() => navigate('/my-route')}>
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
              />
            </svg>
            {t('myProfile.editRoute')}
          </Button>
        </div>

        {employee.homeAddress ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">{t('myProfile.home')}</label>
                <div className="px-4 py-3 bg-surface-secondary border border-slate-200 rounded-lg text-slate-900 break-words">
                  {employee.homeAddress}
                </div>
              </div>
              <div>
                <label className="input-label">{t('myProfile.workplace')}</label>
                <div className="px-4 py-3 bg-surface-secondary border border-slate-200 rounded-lg text-slate-900 break-words">
                  {employee.workAddress || t('myProfile.notSpecified')}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">{t('myProfile.transportMode')}</label>
                <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                  {transportLabels[employee.transportMode] ||
                    employee.transportMode ||
                    t('myProfile.notFilled')}
                </div>
              </div>
              {employee.routeDuration && (
                <div>
                  <label className="input-label">{t('myProfile.routeDuration')}</label>
                  <div className="px-4 py-3 bg-surface-secondary border border-slate-200 dark:border-slate-800 rounded-lg text-slate-900 dark:text-slate-100">
                    {t('myProfile.minutes', { count: employee.routeDuration })}
                  </div>
                </div>
              )}
            </div>
            {employee.routeDetails && (
              <div>
                <label className="input-label">{t('myProfile.routeDetails')}</label>
                <div className="px-4 py-3 bg-surface-secondary border border-slate-200 rounded-lg text-slate-900 break-words whitespace-pre-wrap">
                  {employee.routeDetails}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4 text-slate-400 dark:text-slate-400">
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z"
                />
              </svg>
            </div>
            <p className="text-slate-500 dark:text-slate-400 mb-4">{t('myProfile.noRoute')}</p>
            <Button variant="primary" onClick={() => navigate('/my-route')}>
              {t('myProfile.completeNow')}
            </Button>
          </div>
        )}
      </Card>

      {/* Statistici Instruiri */}
      <Card>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {t('myProfile.statsTitle')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-4xl font-bold text-primary-600 dark:text-primary-300">
              {assigned}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('myProfile.statAssigned')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-accent-600 dark:text-accent-300">
              {completed}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('myProfile.statCompleted')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-purple-600 dark:text-purple-300">
              {completionRate}%
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('myProfile.statRate')}
            </div>
          </div>
        </div>
      </Card>

      {/* Onboarding — buton de redeschidere a wizardului. Util când userul a sărit ghidul
          inițial sau vrea să-l recapituleze (ex: după ce a învățat o parte și vrea altă parte). */}
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('onboarding.reopenWizard.btn')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {t('onboarding.reopenWizard.hint')}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              if (!window.confirm(t('onboarding.reopenWizard.confirmBody'))) return;
              try {
                await api.post('/onboarding/reset');
                // Flag de sesiune: Layout îl detectează la reload și deschide wizardul.
                // Anterior reset-ul nu declanșa nimic — wizardul rămânea ascuns chiar și
                // după ce userul cerea explicit redeschiderea lui.
                sessionStorage.setItem('reopenOnboardingWizard', 'true');
                showToast(t('onboarding.reopenWizard.successToast'), 'success');
                setTimeout(() => window.location.reload(), 800);
              } catch {
                showToast(t('onboarding.reopenWizard.errorToast'), 'error');
              }
            }}
          >
            {t('onboarding.reopenWizard.confirmYes')}
          </Button>
        </div>
      </Card>

      {/* Toggle Push Notifications — disponibil oricui, dacă browser-ul suportă.
          La activare, browser-ul afișează prompt-ul nativ „Permite notificări?". */}
      {pushSupported && (
        <Card>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('myProfile.pushTitle')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('myProfile.pushDesc')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={pushEnabled}
                disabled={pushBusy}
                onChange={togglePush}
              />
              <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </Card>
      )}

      {/* Toggle Reminder IP — doar pentru roluri SSM */}
      {['SUPER_ADMIN', 'SSM_COMPANY_ADMIN', 'SSM_INSTRUCTOR'].includes(user?.role) && (
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('myProfile.ipNotifTitle')}
              </h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                {t('myProfile.ipNotifDesc')}
              </p>
              {reminderEnabled && (
                <button
                  onClick={() => navigate('/employees?tab=ip-due')}
                  className="text-xs text-primary-600 dark:text-primary-300 hover:text-primary-800 font-medium mt-2 inline-flex items-center gap-1"
                >
                  {t('myProfile.configureIntervals')}
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </button>
              )}
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={reminderEnabled}
                onChange={handleToggleReminder}
              />
              <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>
        </Card>
      )}

      {/* Notificări Email */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {t('myProfile.emailNotifTitle')}
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                {t('myProfile.emailNotifReceive')}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {user?.role === 'EMPLOYEE'
                  ? t('myProfile.emailNotifDescEmployee')
                  : t('myProfile.emailNotifDescOther')}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={emailNotifEnabled}
                onChange={handleToggleEmailNotif}
              />
              <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
            </label>
          </div>

          {emailNotifEnabled && user?.role !== 'EMPLOYEE' && (
            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {t('myProfile.emailDigestReceive')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('myProfile.emailDigestDesc')}
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={emailDigestEnabled}
                  onChange={handleToggleEmailDigest}
                />
                <div className="w-11 h-6 bg-slate-200 dark:bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 dark:after:border-slate-700 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          )}
        </div>
      </Card>

      {/* GDPR — datele mele (export + ștergere/anonimizare cont) */}
      <Card>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('gdprData.title')}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xl">
              {t('gdprData.subtitle')}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" onClick={handleExportData} loading={exportingData}>
            {t('gdprData.exportBtn')}
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setDeletePassword('');
              setShowDeleteAccount(true);
            }}
          >
            {t('gdprData.deleteBtn')}
          </Button>
        </div>
      </Card>

      <Modal
        open={showReqName}
        onClose={() => !submittingName && setShowReqName(false)}
        closable={!submittingName}
        title={t('myProfile.nameModalTitle')}
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowReqName(false)}
              disabled={submittingName}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitNameRequest}
              loading={submittingName}
              disabled={submittingName || !reqNameNew.trim()}
            >
              {t('myProfile.submitRequest')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>
            <Trans i18nKey="myProfile.nameModalIntro" components={{ b: <strong /> }} />
          </p>
          <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
            <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
              {t('myProfile.currentNameLabel')}
            </p>
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {user.firstName} {user.lastName}
            </p>
          </div>
          <Input
            label={t('myProfile.newLastNameLabel')}
            value={reqNameNew}
            onChange={(e) => setReqNameNew(e.target.value)}
            placeholder={t('myProfile.namePlaceholder')}
            required
          />
          <Input
            label={t('myProfile.reasonOptionalLabel')}
            value={reqNameReason}
            onChange={(e) => setReqNameReason(e.target.value)}
            placeholder={t('myProfile.reasonNamePlaceholder')}
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('myProfile.supportingDocLabel')}
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setReqNameFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-slate-700 dark:text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              {t('myProfile.supportingDocNameHint')}
            </p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
              {t('myProfile.whatHappensTitle')}
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5 list-disc pl-5">
              <li>{t('myProfile.whatHappensRequestLogged')}</li>
              <li>{t('myProfile.whatHappensSignPopup')}</li>
              <li>{t('myProfile.nameWhatHappensNameUpdated')}</li>
              <li>{t('myProfile.nameWhatHappensOldNameCrossed')}</li>
              <li>{t('myProfile.whatHappensAnnexAtEnd')}</li>
              <li>{t('myProfile.nameWhatHappensDocArchived')}</li>
              <li>{t('myProfile.nameWhatHappensCanChangeSignature')}</li>
            </ul>
          </div>
        </div>
      </Modal>

      <Modal
        open={showReqRoute}
        onClose={() => !submittingRoute && setShowReqRoute(false)}
        closable={!submittingRoute}
        title={t('myProfile.requestRouteChange')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowReqRoute(false)}
              disabled={submittingRoute}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitRouteRequest}
              loading={submittingRoute}
              disabled={submittingRoute}
            >
              {t('myProfile.submitRequest')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('myProfile.routeModalIntro')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label={t('myProfile.homeAddressLabel')}
              value={reqRouteForm.homeAddress}
              onChange={(e) => setReqRouteForm({ ...reqRouteForm, homeAddress: e.target.value })}
            />
            <Input
              label={t('myProfile.workAddressLabel')}
              value={reqRouteForm.workAddress}
              onChange={(e) => setReqRouteForm({ ...reqRouteForm, workAddress: e.target.value })}
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('myProfile.transportMode')}
              </label>
              <select
                value={reqRouteForm.transportMode}
                onChange={(e) =>
                  setReqRouteForm({ ...reqRouteForm, transportMode: e.target.value })
                }
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
              >
                <option value="">{t('myProfile.selectPlaceholder')}</option>
                <option value="personal">{t('myProfile.transport.personal')}</option>
                <option value="public">{t('myProfile.transport.public')}</option>
                <option value="mixt">{t('myProfile.transportMixtOption')}</option>
                <option value="pe_jos">{t('myProfile.transport.walk')}</option>
                <option value="bicicleta">{t('myProfile.transport.bike')}</option>
              </select>
            </div>
            <Input
              label={t('myProfile.routeDurationInputLabel')}
              type="number"
              value={reqRouteForm.routeDuration}
              onChange={(e) => setReqRouteForm({ ...reqRouteForm, routeDuration: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('myProfile.routeDescLabel')}
            </label>
            <textarea
              rows={3}
              value={reqRouteForm.routeDetails}
              onChange={(e) => setReqRouteForm({ ...reqRouteForm, routeDetails: e.target.value })}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            />
          </div>
          <Input
            label={t('myProfile.reasonOptionalLabel')}
            value={reqRouteReason}
            onChange={(e) => setReqRouteReason(e.target.value)}
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('myProfile.supportingDocLabel')}
            </label>
            <input
              type="file"
              accept=".pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => setReqRouteFile(e.target.files?.[0] || null)}
              className="block w-full text-sm"
            />
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              {t('myProfile.supportingDocRouteHint')}
            </p>
          </div>
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
            <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">
              {t('myProfile.whatHappensTitle')}
            </p>
            <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-0.5 list-disc pl-5">
              <li>{t('myProfile.whatHappensRequestLogged')}</li>
              <li>{t('myProfile.whatHappensSignPopup')}</li>
              <li>{t('myProfile.routeWhatHappensRouteUpdated')}</li>
              <li>{t('myProfile.routeWhatHappensOldRouteCrossed')}</li>
              <li>{t('myProfile.whatHappensAnnexAtEnd')}</li>
              <li>{t('myProfile.routeWhatHappensDocArchived')}</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Modal schimbare parolă (Bug #5) */}
      {showChangePassword && (
        <ChangePasswordModal
          onClose={() => setShowChangePassword(false)}
          onSuccess={() => setShowChangePassword(false)}
        />
      )}

      {/* GDPR — confirmare ștergere cont (cere parola) */}
      <Modal
        open={showDeleteAccount}
        onClose={() => !deletingAccount && setShowDeleteAccount(false)}
        closable={!deletingAccount}
        title={t('gdprData.deleteTitle')}
        size="md"
        footer={
          <div className="flex gap-3 justify-end w-full">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteAccount(false)}
              disabled={deletingAccount}
            >
              {t('common.cancel', 'Anulează')}
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteAccount}
              loading={deletingAccount}
              disabled={deletingAccount || !deletePassword}
            >
              {t('gdprData.deleteConfirmBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <Alert type="warning">{t('gdprData.deleteWarning')}</Alert>
          <p>{t('gdprData.deletePasswordPrompt')}</p>
          <Input
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            placeholder={t('gdprData.passwordPlaceholder')}
            autoComplete="current-password"
          />
        </div>
      </Modal>
    </div>
  );
};

export default MyProfile;
